import _ from 'lodash';
import Combinatorics from 'js-combinatorics';

import TalentTraits from 'shared/talentTraits';
import { deepFreeze } from 'shared/util';

/*

Expand multi-point nodes into their own nodes in the graph. Makes it a bit easier to traverse
and not really need special casing.

Also adds some additional metadata to the graph for use in later traversal algorithms

*/
const getExpandedGraph = tree => {
  const graphByKey = {};

  // track data for DAG topographical sort and also expand multirank nodes into their own nodes
  let index = 0;
  _.each(tree, node => {
    if (node.maxRanks === 1) {
      const key = `${node.id}:1`;
      graphByKey[key] = {
        key,
        // eslint-disable-next-line no-plusplus
        index: index++,
        ...node,
        reqPoints: node.reqPoints || 0,
        rank: 1,
        adj: _.map(node.next, nodeId => `${nodeId}:1`),
        indegree: 0,
        connected: 0,
      };
    } else {
      for (let i = 0; i < node.maxRanks; i += 1) {
        const rank = i + 1;
        const key = `${node.id}:${rank}`;
        if (rank < node.maxRanks) {
          graphByKey[key] = {
            key,
            // eslint-disable-next-line no-plusplus
            index: index++,
            ...node,
            reqPoints: node.reqPoints || 0,
            rank,
            adj: [`${node.id}:${rank + 1}`],
            indegree: 0,
            connected: 0,
          };
        } else {
          graphByKey[key] = {
            key,
            // eslint-disable-next-line no-plusplus
            index: index++,
            ...node,
            reqPoints: node.reqPoints || 0,
            rank,
            adj: _.map(node.next, nodeId => `${nodeId}:1`),
            indegree: 0,
            connected: 0,
          };
        }
      }
    }
  });

  // initialize indegree for each node.
  // indegree is a count of how many nodes are linking into this one. As nodes are visited in algorithms below,
  // this is decremented as a quick way to determine candidate notes for next traversal
  _.each(graphByKey, node => {
    _.each(node.adj, key => {
      graphByKey[key].indegree += 1;
    });
  });

  const graph = [];

  // remap into an array
  _.each(graphByKey, node => {
    graph[node.index] = _.omit({
      ...node,
      // remap the original adjancency array with array indices rather than full trait IDs
      adjacent: _.map(node.adj, key => graphByKey[key].index),
      // get rid of some data we don't really care about
    }, ['posX', 'posY', 'next', 'adj']);
  });

  return graph;
};

/*
  get a topographical sort of the DAG using kahns algorithm

  https://en.wikipedia.org/wiki/Topological_sorting#Kahn's_algorithm
*/
const kahns = (tree) => {
  // we destructively use indegree in this algorithm so save the original value to restore at the end
  const graph = _.map(
    getExpandedGraph(tree),
    n => ({
      ...n,
      origIndegree: n.indegree,
    }),
  );

  const sorted = [];
  let candidates = _.sortBy(_.filter(graph, { indegree: 0 }), 'reqPoints');

  while (candidates.length > 0) {
    // always re-sort to get lower reqPoints nodes first
    candidates = _.sortBy(candidates, 'reqPoints');
    const candidate = candidates.shift();

    sorted.push(candidate);
    for (let u = 0; u < candidate.adjacent.length; u += 1) {
      const nextCandidate = graph[candidate.adjacent[u]];
      nextCandidate.indegree -= 1;
      if (nextCandidate.indegree === 0) {
        // process rank 2 of a node before children
        if (candidate.id === nextCandidate.id) {
          candidates.unshift(nextCandidate);
        } else {
          candidates.push(nextCandidate);
        }
      }
    }
  }

  // since we have re-sorted the graph, we need to update the index values in the nodes
  // also need to restore some data from the original graph
  const indexMap = _.fromPairs(_.map(sorted, (node, index) => ([node.index, index])));

  return _.map(
    sorted,
    (node, index) => ({
      ..._.omit(node, ['origIndegree']),
      index,
      indegree: node.origIndegree,
      // remap adjacent to new indices
      adjacent: _.map(node.adjacent, idx => indexMap[idx]),
    }),
  );
};

const getActiveNodeObj = _.memoize((id, rank, entryId) => {
  const obj = {
    id,
    rank,
    entryId,
  };
  deepFreeze(obj);
  return obj;
}, (...args) => {
  return _.join(args, '-');
});

/*
  Traverse all possible paths through the graph that has been sorted using Kahn's
  Only visit nodes that are higher in the sorted graph - this ensures no repeat visits.

  Does NOT treat choice/selection nodes as 2 different nodes. Choice expansion is handled in a second phase.

  This algorithm uses recursive backtracking and stores minimal data which is nice.

  It also uses a minimal representation of the path by using a single Number as a 64-bit value. Each "bit"
  is a visited node index in the incoming sorted graph. This is way smaller than an array and requires less
  explicit array copying. Might be a pain to unpack later since it depends on the order of the incoming, sorted
  graph.
*/
export function topoSortWithAdjacentsKahns(
  tree,
  budget,
  {
    picked = [], // array of entryId:rank string
    exclude = [], // array of node IDs
    maxCombos = Infinity,
    maxTime = null,
    store = true,
  },
) {
  const startTime = Date.now();
  const graph = kahns(tree);

  const rawCombos = [];
  const paths = [];
  const results = [];
  const active = _.fromPairs(
    _.map(
      _.uniqBy(graph, 'id'),
      node => ([
        node.id,
        {
          id: node.id,
          rank: 0,
          entryId: null,
        },
      ]),
    ),
  );
  let count = 0;

  const visited = _.map(_.range(0, graph.length), false);
  const excludeSet = new Set(exclude);
  const excluded = _.map(_.range(0, graph.length), i => {
    return excludeSet.has(graph[i].id);
  });
  const path = [];
  let pathKey = 0;

  function visitNode(index, entryId) {
    const node = graph[index];
    for (let u = 0; u < node.adjacent.length; u += 1) {
      graph[node.adjacent[u]].indegree -= 1;
      graph[node.adjacent[u]].connected += 1;
    }

    visited[index] = true;
    pathKey += 2 ** index;
    path.push(node);
    if (!node.freeNode) {
      active[node.id].rank += 1;
      active[node.id].entryId = entryId;
    }
  }
  function leaveNode(index) {
    const node = graph[index];
    for (let u = 0; u < node.adjacent.length; u += 1) {
      graph[node.adjacent[u]].indegree += 1;
      graph[node.adjacent[u]].connected -= 1;
    }

    visited[index] = false;
    pathKey -= 2 ** index;
    path.pop();
    if (!node.freeNode) {
      active[node.id].rank -= 1;
      if (active[node.id].rank === 0) {
        active[node.id].entryId = null;
      }
    }
  }

  let calls = 0;
  function walk(start = 0, spent = 0) {
    calls += 1;
    if (maxTime && (calls % 10000 === 0) && (Date.now() - startTime > maxTime)) {
      throw new Error('max_time_exceeded');
    }

    if (budget === spent) {
      count += 1;
      if (count > maxCombos) {
        throw new Error('max_combos_exceeded');
      }
      if (store) {
        rawCombos.push(pathKey);
        paths.push([...path]);
        results.push(
          _.filter(
            _.map(
              active,
              (obj) => getActiveNodeObj(obj.id, obj.rank, obj.entryId),
            ),
            n => n.rank,
          ),
        );
      }
    } else {
      const len = graph.length;
      const pointsLeft = budget - spent;
      const nodesLeft = len - start;
      // not enough node left to finish a build
      if (pointsLeft > nodesLeft) {
        return;
      }
      for (let v = start; v < len; v += 1) {
        const node = graph[v];
        // indegree 0 indicates a node ready for entry
        // connected > 0 means something is adjacent
        //
        // connected ends up being necessary because we have a point budget - topo sort algorithms
        // are usually traversing _all_ possible paths, not just ones that visit a subset of the nodes.
        // Tracking connected is faster than iterating over all visited node adjacency lists every time.
        if (!visited[v] && !excluded[v] && (node.indegree === 0 || node.connected > 0)) {
          if (node.reqPoints <= spent) {
            for (let e = 0; e < node.entries.length; e += 1) {
              const entryId = node.entries[e].id;
              visitNode(v, entryId);

              walk(v + 1, spent + 1);

              leaveNode(v, entryId);
            }
          }
        }
      }
    }
  }

  // preprocess any free nodes
  for (let i = 0; i < graph.length; i += 1) {
    const node = graph[i];
    if (node.freeNode) {
      visitNode(i);
    }
  }

  let spent = 0;

  if (picked) {
    // expand multi-rank nodes to match the graph
    const pickedExpanded = [];
    for (let i = 0; i < picked.length; i += 1) {
      for (let j = 0; j < picked[i].rank; j += 1) {
        pickedExpanded.push({
          ...picked[i],
          rank: j + 1,
        });
      }
    }

    for (let i = 0; i < pickedExpanded.length; i += 1) {
      const pick = pickedExpanded[i];
      const key = _.join([pick.entry.nodeId, pick.rank], ':');
      for (let j = 0; j < graph.length; j += 1) {
        if (graph[j].key === key) {
          spent += 1;
          visitNode(j, pick.entry.id);
          break;
        }
      }
    }
  }

  walk(0, spent);

  return {
    paths,
    results,
    combos: rawCombos,
    count,
    time: Date.now() - startTime,
    calls,
  };
}


function getTalentCombinations(
  talents,
  level,
  opts,
) {
  const { exclude, maxCombos, maxTime, format, render } = _.defaults(
    opts,
    {
      exclude: [],
      maxCombos: 10000,
      maxTime: 1000,
      format: undefined,
      // true: return array of talent strings. false: return array of nulls to get the count on the frontend
      render: true,
    },
  );
  const tree = TalentTraits.getTreeBySpecId(talents.specId);

  const budgets = TalentTraits.getPointsForLevel(level);

  const specCombos = topoSortWithAdjacentsKahns(
    tree.specNodes,
    budgets.spec,
    {
      picked: talents.entries,
      exclude,
      maxCombos,
      maxTime,
    },
  );

  const classCombos = topoSortWithAdjacentsKahns(
    tree.classNodes,
    budgets.class,
    {
      picked: talents.entries,
      exclude,
      maxCombos,
      maxTime,
    },
  );

  // cartesian product the spec and class tree combos

  if (specCombos.count * classCombos.count > maxCombos) {
    throw new Error('combined_max_combos_exceeded');
  }

  // we'll reuse this object and just mutate the .entries array
  const talentsTmp = _.cloneDeep(talents);
  talentsTmp.rawInput = null;
  talentsTmp.rawString = null;
  talentsTmp.meta = 'Do not use this object. Only intended for internal use in talentCombinations';

  const product = Combinatorics.cartesianProduct(classCombos.results, specCombos.results);
  const combinations = product.map(combo => {
    if (render) {
      const entries = [...combo[0], ...combo[1]];
      // create a minimal entry required for talent stringification
      talentsTmp.entries = _.map(entries, entry => ({
        entry: {
          id: entry.entryId,
          nodeId: entry.id,
        },
        rank: entry.rank,
      }));
      return TalentTraits.stringify(talentsTmp, format);
    }

    return null;
  });

  return combinations;
}

export default getTalentCombinations;
